import { useParams } from 'react-router-dom';
import { useQueryClient, useJobStatus, JobIdResponse } from 'shared-ui';
import { toast } from 'react-toastify';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { deleteGroupTerminalPermissions } from 'api/permissions/permissions';
import { useSelectedGroupTerminalPermission } from 'hooks/useSelectedGroupTerminalPermission/useSelectedGroupTerminalPermission';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';

import { DeleteGroupTerminalPermission } from './DeleteGroupTerminalPermission';
import { DeleteGroupTerminalPermissionContainerProps } from './DeleteGroupTerminalPermission.types';

const isJobResponse = (response: JobIdResponse | void): response is JobIdResponse => {
  return response !== undefined && response !== null && typeof response === 'object' && 'jobId' in response;
};

export const DeleteGroupTerminalPermissionContainer = ({
  selectedPermission,
}: DeleteGroupTerminalPermissionContainerProps) => {
  const { id } = useParams<{ id: string }>();
  const groupId = id || '';
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { setSelectedGroupTerminalPermission } = useSelectedGroupTerminalPermission();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const jobStatus = useJobStatus({
    autoClose: false,
    useGlobalModal: true,
    showItemCount: false,
    onComplete: () => {
      setSelectedGroupTerminalPermission(undefined);
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
      toast.dark(formatMessage({ id: AppMessages['group.reader.permission.delete.success'] }));
    },
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['groups.result.title'] }),
  });

  const { mutate: onDeleteGroupTerminalPermissions, isLoading } = useMutation(deleteGroupTerminalPermissions, {
    onSuccess: (response) => {
      if (isJobResponse(response)) {
        jobStatus.startJob(response.jobId);
        onModalClose();
      } else {
        // Fallback for synchronous response (shouldn't happen with threshold 1)
        setSelectedGroupTerminalPermission(undefined);
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
        toast.dark(formatMessage({ id: AppMessages['group.reader.permission.delete.success'] }));
        onModalClose();
      }
    },
  });

  const onSubmit = () => {
    onDeleteGroupTerminalPermissions({
      permissionId: selectedPermission.id,
      groupId,
    });
  };

  return (
    <DeleteGroupTerminalPermission
      isModalOpen={isModalOpen}
      onModalClose={onModalClose}
      onModalOpen={onModalOpen}
      isLoading={isLoading}
      onSubmit={onSubmit}
    />
  );
};
